import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";

import healthImage from "assets/health.svg";
import Bar from "components/base/Bar";
import MedallionsInfo from "components/base/MedallionsInfo";
import { damagedAnimation } from "components/styles/animations";
import { media } from "components/styles/breakpoints";
import { isFightModeStatus } from "libs/fight";
import { getPilotData, getShipData, hasAnimation } from "libs/stats";
import { enterShip } from "redux/actions";
import {
  getBuildInfo,
  getDerivedStats,
  getShipDisplayInfo,
  getShipHealth,
} from "redux/selectors/characterSelectors";
import { getFightStatus } from "redux/selectors/fightSelectors";
import { COLORS } from "utils/constants";
import AccountWarning from "./AccountWarning";

interface PilotImageWrapperProps {
  $animations: string[];
  isFighting: boolean;
}

interface PilotBackgroundProps {
  color: string;
}

const CharacterInfoWrapper = styled.div`
  position: absolute;
  top: 3dvh;
  left: 3dvh;
  z-index: 6;

  ${media.desktop`
    top: 5dvh;
    left: 5dvh;
  `}
`;

const StatusWrapper = styled.div`
  display: flex;
  align-items: center;
  height: clamp(50px, 10vh, 80px);
`;

const damagedMixin = css`
  animation: ${damagedAnimation} 300ms ease-in-out forwards;
`;

const MainInfoWrapper = styled.div`
  height: 100%;
  aspect-ratio: 1/1;
  z-index: 1;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PilotImageWrapper = styled.div<PilotImageWrapperProps>`
  height: 100%;
  z-index: 1;
  cursor: pointer;
  ${(props) => !!props.isFighting && "pointer-events: none;"}
  ${(props) =>
    hasAnimation(props.$animations, "damaged") ? damagedMixin : ""};
`;

const PilotBackground = styled.div<PilotBackgroundProps>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  z-index: -1;
  background-color: ${(props) => props.color};
  filter: brightness(40%);
`;

const PilotImage = styled.img`
  height: 100%;
`;

const BarsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: -6px;
`;

const BarWrapper = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const HealthBarWrapper = styled(BarWrapper)`
  width: 200px;
  ${media.desktop`
    width: 280px;
  `}
`;

const MedallionsInfoWrapper = styled(BarWrapper)`
  width: 191px;
  ${media.desktop`
    width: 268px;
  `}
`;

const AccountWarningWrapper = styled.div`
  margin-left: 30px;
`;

export default function CharacterInfo() {
  const dispatch = useDispatch();

  const derivedStats = useSelector(getDerivedStats);
  const { animations } = useSelector(getShipDisplayInfo);
  const health = useSelector(getShipHealth);
  const { ship, pilot } = useSelector(getBuildInfo);
  const status = useSelector(getFightStatus);

  const isFightMode = isFightModeStatus(status);
  const { color } = getShipData(ship);
  const { images: pilotImages } = getPilotData(pilot);

  return (
    <CharacterInfoWrapper>
      <StatusWrapper>
        <MainInfoWrapper>
          <PilotImageWrapper
            $animations={animations}
            isFighting={isFightMode}
            onClick={() => dispatch(enterShip())}
          >
            <PilotBackground color={color} />
            <PilotImage src={pilotImages.avatar} />
          </PilotImageWrapper>
        </MainInfoWrapper>
        <BarsWrapper>
          <HealthBarWrapper>
            <Bar
              icon={healthImage}
              number={health}
              maxNumber={derivedStats.current.complete.maxHealth}
              color={COLORS.HEALTH}
              name="Ship Health"
              description="This is how much health your ship currently has. Don't let it drop to zero! You can fully repair your starship at the nearest Loadout Hub in any city."
            />
          </HealthBarWrapper>
          <MedallionsInfoWrapper>
            <MedallionsInfo />
          </MedallionsInfoWrapper>
        </BarsWrapper>
        <AccountWarningWrapper>
          <AccountWarning />
        </AccountWarningWrapper>
      </StatusWrapper>
    </CharacterInfoWrapper>
  );
}
